import React, { FC } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import classNames from 'classnames';

import { icon_chevron_down, icon_chevron_up, icon_gitt } from '@gn-audio/icons';
import { getGiftWrapping, ProductService } from '@gn-audio/jss-core';
import { getLocaleWithRegion } from '@gn-audio/jss-core/common/utilities';

import { useAsync, useFallbackTranslation } from '../../../utils/hooks';
import { Button } from '../../Button/Button';
import { JabraIcon } from '../../JabraIcon/JabraIcon';
import { useHeaderNavigation } from '../../MegaMenu';
import GiftWrappingSkeletonLoader from './GiftWrappingSkeletonLoader';
import { getGiftWrappingDetailsForGroup } from './giftWrappingUtils';

import basketStyles from '../Basket.module.css';

export type GiftWrappingComponentVariants = 'outlined' | 'normal';
export interface GiftWrappingProps {
  hasGiftWrapping?: boolean;
  groupId?: number;
  cssClass?: string;
  variant?: GiftWrappingComponentVariants;
  onAddGiftWrapping?: (service: ProductService) => void;
  onRemoveGiftWrapping?: () => void;
}

export const GiftWrapping: FC<GiftWrappingProps> = ({
  onRemoveGiftWrapping,
  onAddGiftWrapping,
  hasGiftWrapping,
  cssClass,
  groupId,
  variant = 'normal',
}) => {
  const [open, setOpen] = React.useState(false);
  const { currentLocale } = useHeaderNavigation();
  const locale = getLocaleWithRegion(currentLocale);

  const { t } = useFallbackTranslation();

  const config = useAsync(() => getGiftWrapping(locale), [locale]);

  const giftWrappingDetails = useAsync(async () => {
    if (groupId === undefined) return;
    return getGiftWrappingDetailsForGroup(groupId, locale);
  }, [groupId]);

  const handleToggleGiftWrapping = () => {
    if (hasGiftWrapping) {
      if (onRemoveGiftWrapping) {
        onRemoveGiftWrapping();
      }
      return;
    }
    if (config && giftWrappingDetails) {
      const service: ProductService = {
        serviceProductId: giftWrappingDetails.sku,
        value: config.giftWrappingD365String,
      };
      if (onAddGiftWrapping) {
        onAddGiftWrapping(service);
      }
    }
  };
  const isOutlined = variant === 'outlined';

  if (giftWrappingDetails === undefined) {
    return <GiftWrappingSkeletonLoader />;
  }

  if (!config || giftWrappingDetails === null) {
    return null;
  }

  const [gifImage] = giftWrappingDetails?.images ?? [];

  const containerClass = isOutlined ? '' : classNames(basketStyles.card, basketStyles.grey);
  const buttonIconClass = isOutlined ? 'h-4 w-4' : '';
  const headerTextClass = isOutlined ? 'font-bold text-gray-700 text-[1.125rem]' : '';
  const collapseBodyClass = isOutlined
    ? 'border border-gray-100 p-6 rounded-[0.5rem] my-5'
    : 'pt-[0.625rem] mt-[1rem]';
  return (
    <div className={cssClass}>
      <Collapsible.Root className={containerClass} open={open} onOpenChange={setOpen}>
        <div className="flex flex-wrap items-center gap-3">
          <Collapsible.Trigger asChild>
            <div className="flex flex-1 items-center gap-3">
              <Button
                icon={
                  open ? (
                    <JabraIcon className={buttonIconClass} Icon={icon_chevron_up} />
                  ) : (
                    <JabraIcon className={buttonIconClass} Icon={icon_chevron_down} />
                  )
                }
                variant="tertiary"
                size={isOutlined ? 's' : 'm'}
              />
              <span className={classNames(headerTextClass, 'text-nowrap')}>
                {hasGiftWrapping
                  ? t('cart.GiftWrappingAddedHeadline')
                  : t('cart.GiftWrappingHeadline')}
              </span>
            </div>
          </Collapsible.Trigger>
          <Button
            variant={variant === 'outlined' ? 'secondary' : 'tertiary'}
            size="s"
            onClick={handleToggleGiftWrapping}
          >
            {!hasGiftWrapping && <span>{t('cart.GiftWrappingAdd')}</span>}
            {hasGiftWrapping && <span>{t('cart.GiftWrappingRemove')}</span>}
          </Button>
        </div>

        <Collapsible.Content className="collapsible-content">
          <div className={classNames('flex gap-6', collapseBodyClass)}>
            {gifImage ? (
              <img
                src={gifImage.url}
                alt={t('cart.GiftWrappingInfoBoxHeadline')}
                className="h-16 w-16"
              />
            ) : (
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-primary">
                <JabraIcon className="h-12 w-12 text-on-brand" Icon={icon_gitt} />
              </div>
            )}

            <div className="flex flex-col gap-3">
              <h2 className="text-[1.25rem] font-bold">{t('cart.GiftWrappingInfoBoxHeadline')}</h2>
              <p className="text-[0.75rem]">{t('cart.GiftWrappingInfoBoxText')}</p>
              <span className="flex gap-4 font-bold">
                <span
                  className="text-[0.75rem]"
                  dangerouslySetInnerHTML={{ __html: t('cart.GiftWrappingTermsAndConditions') }}
                ></span>
              </span>
            </div>
          </div>
        </Collapsible.Content>
      </Collapsible.Root>
    </div>
  );
};
